import '@shui/core/sh-popover';

import {
    Component,
    input,
    booleanAttribute,
    output,
    ChangeDetectionStrategy,
    afterEveryRender,
    inject,
    ElementRef
} from '@angular/core';

@Component({
    selector: 'sh-popover',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.accessible-type-label]': 'accessibleTypeLabel() ?? null',
        '[attr.always-stay-within]': 'alwaysStayWithin() ? "" : null',
        '[attr.blur-background]': 'blurBackground() ? "" : null',
        '[attr.icon]': 'icon() ?? null',
        '[attr.label]': 'label() ?? null',
        '[attr.label-rows]': 'labelRows() ?? null',
        '[attr.manual-controlled]': 'manualControlled() ? "" : null',
        '[attr.mobile]': 'mobile() ? "" : null',
        '[attr.passive]': 'passive() ? "" : null',
        '[attr.position]': 'position() ?? null',
        '[attr.position-fixed-adjustment]': 'positionFixedAdjustment() ? "" : null',
        '[attr.scroll-action]': 'scrollAction() ?? null',
        '[attr.sticky]': 'sticky() ? "" : null',
        '[attr.suppress-placement-warning]': 'suppressPlacementWarning() ? "" : null',
        '[attr.target]': 'target() ?? null',
        '[attr.track-target-on]': 'trackTargetOn() ?? null',
        '[attr.type]': 'type() ?? null',
        '[attr.visible]': 'visible() ? "" : null',
        '(overlay-closed)': 'overlayClosed.emit($event)'
    }
})
export class ShPopoverComponent {
    private readonly elementRef =
        inject<ElementRef<HTMLElement & { manualControlled?: boolean; visible?: boolean }>>(ElementRef);

    /** Defining the accessible-type-label property to set the aria-label for the icon types */
    public readonly accessibleTypeLabel = input<string | number | undefined>(undefined);

    /** Forces popover to stay in visible viewport when enabled */
    public readonly alwaysStayWithin = input(false, { transform: booleanAttribute });

    /** If set to `true`, the popover will have a semi-transparent background. */
    public readonly blurBackground = input(false, { transform: booleanAttribute });

    /**
     * Defines the popover icon. If a type is specified, the icon property will be overridden by the
     * type.
     */
    public readonly icon = input<string | number | undefined>(undefined);

    /** Defines the label of the popover that will be displayed on top of the content. */
    public readonly label = input<string | number | undefined>(undefined);

    /** Allows longer labels to be used. Possible value 'auto'. */
    public readonly labelRows = input<string | number | undefined>(undefined);

    /** Allows consumers to control the popover visibility explicitly. */
    public readonly manualControlled = input(false, { transform: booleanAttribute });

    /** If set to `true`, the popover will show styles applicable to mobile view */
    public readonly mobile = input(false, { transform: booleanAttribute });

    /** If set to `true`, the popover will show a pointing arrow. */
    public readonly passive = input(false, { transform: booleanAttribute });

    /**
     * Defines where the tooltip will be shown. Accepted values are - top, bottom, left, right, top-
     * right, top-left, bottom-left, bottom-right, left-up, left-down, right-up, right-down
     */
    public readonly position = input<string | number | undefined>(undefined);

    /**
     * This component has position:fixed as its css. So when giving left/top to the component, it
     * will be with respect to viewport. But if the parent element has either transform/perspective
     * or other properties, then this component will position with respect to that parent
     * component. Refer position fixed in https://developer.mozilla.org/en-
     * US/docs/Web/CSS/position?v=example#values and https://developer.mozilla.org/en-
     * US/docs/Web/CSS/Containing_block#identifying_the_containing_block This might position this
     * component somewhere which is not desirable. So set this property to true, if any parent
     * element has any of this css to correctly readjust this component to position with respect to
     * the viewport. Also makes the popover stay in the same position while applying zoom.
     */
    public readonly positionFixedAdjustment = input(false, { transform: booleanAttribute });

    /** Decides the behavior of popover when scrolled. Accepted values are 'refit' and 'hide' */
    public readonly scrollAction = input<string | number | undefined>(undefined);

    /**
     * If set to `true`, the popover will not close when the user clicks on the page. It can be
     * closed through actions present in the popover
     */
    public readonly sticky = input(false, { transform: booleanAttribute });

    /** Will suppress placement warnings when enabled. */
    public readonly suppressPlacementWarning = input(false, { transform: booleanAttribute });

    /**
     * Defines the element to which the popover positions itself. If manualControlled property is
     * set as false, then popover toggles its visiblity on clicking the target (hovering the target
     * in case of submenu). Target property can be string corresponding to id of the target or a
     * direct DOM reference.
     */
    public readonly target = input<string | number | undefined>(undefined);

    /**
     * Decides if the popover tracks the target position-change on scroll of target or on any
     * movement of the target . The target position-change is normally tracked when there is a
     * resize of the target and window resize. In addition to this: If the value is `scrolling`,
     * then the target position-change is tracked if it is due to scrolling. If the value is `any`,
     * the target position change is tracked whenever the target changes position. The value of
     * `any` is not needed in most cases. If the value of `any` is used, internally
     * requestAnimationFrame will be used to track the target and is performance intensive. (Will
     * cause change detections if framework is Angular)
     */
    public readonly trackTargetOn = input<string | number | undefined>(undefined);

    /**
     * Defines the type of popover that will be displayed. Accepted values are error, alert and
     * confirmation
     */
    public readonly type = input<string | number | undefined>(undefined);

    /** Defines if the popover is shown (true) or not (false) */
    public readonly visible = input<boolean>();

    public readonly overlayClosed = output<Event>();

    constructor() {
        afterEveryRender(() => {
            const element = this.elementRef.nativeElement;
            element.manualControlled = this.manualControlled();
            const visible = this.visible();
            if (visible !== undefined) {
                element.visible = visible;
            }
        });
    }
}
